import React, { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Input,
  Label,
  Form,
  FormGroup
} from 'reactstrap';
import SelectInput from '../../common/SelectInput';
import FormInput from '../../common/FormInput';
import { useCreateProductMutation } from '../../../redux/productApiSlice';
import { useProductLov } from '../../../hooks/useProductLov';
import AssociateVendorModal from '../vendorModals/AssociateVendorModal';
import AssociateManufacturerModal, { ManufacturerData } from '../brand/AssociateManufacturerModal';
import { VendorData } from 'features/organizations';
import { transformFormDataToCreateProductData } from 'features/products/utils/productDataMappers';
import { ConfirmationModal } from 'Components/Common/DeleteModal';

export interface CreateProductFormData {
  name: string;
  productCode: string;
  vendor: string;
  vendorProductCode: string;
  type: string;
  subtype: string;
  marginType: string;
  category: string;
  subcategory: string;
  active: boolean;
  orderable: boolean;
  taxable: boolean;
  description: string;
  brand: string;
  brandId: number;
  brandProductCode: string;
  barcode: string;
  orderQty: string;
  purchaseExc: string;
  purchaseTax: string;
  purchaseInc: string;
  markup: string;
  sellExc: string;
  sellTax: string;
  sellInc: string;
  nzStatus: string;
  addToAll: boolean;
  pickQuantity: string;
  priceList: boolean;
  location: boolean;
  dangerousGoods: boolean;
  vendorId: number;
}

interface NewProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const defaultForm: CreateProductFormData = {
  name: '',
  productCode: '',
  vendor: '',
  vendorProductCode: '',
  type: '',
  subtype: '',
  marginType: '',
  category: '',
  subcategory: '',
  active: true,
  orderable: true,
  taxable: true,
  description: '',
  brand: '',
  brandId: 0,
  brandProductCode: '',
  barcode: '',
  orderQty: '',
  purchaseExc: '',
  purchaseTax: 'GST - 10%',
  purchaseInc: '',
  markup: '',
  sellExc: '',
  sellTax: 'GST - 10%',
  sellInc: '',
  nzStatus: 'Always Available',
  addToAll: false,
  pickQuantity: '1',
  priceList: true,
  location: true,
  dangerousGoods: false,
  vendorId: 0,
};

const purchaseTaxOptions = ['GST - 10%', 'GST - 0%', 'None'];
const sellTaxOptions = ['GST - 10%', 'GST - 0%', 'None'];
const nzStatusOptions = ['Always Available', 'Not Available', 'Limited'];

const NewProductsModal: React.FC<NewProductsModalProps> = ({ isOpen, onClose }) => {

  // State for the form
  const [form, setForm] = useState<CreateProductFormData>(defaultForm);

  // Add modal state variables
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);

  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  // track form errors
  const [errors, setErrors] = useState<{ name?: string }>({});
  const isNameValid = useMemo(() => form.name.trim().length > 0, [form.name]); // to validate name



  // calling LOV hook to set list of values
  const {
    isReady,
    error,
    retry,
    typeOptions,
    categoryOptions,
    marginTypeOptions,
    getSubTypesForType,
    getSubCategoriesForCategory
  } = useProductLov();


  //calling create product api
  const [createProductApi, { isLoading: isCreating }] = useCreateProductMutation();

  // Calculate subtype options using useMemo
  const subtypeOptions = useMemo(() => {
    if (!form.type || !isReady) return [];

    const selectedType = typeOptions.find(option => option.label === form.type);
    if (!selectedType) return [];

    const subtypes = getSubTypesForType(selectedType.value);
    return subtypes.map(item => ({ value: item.rowId, label: item.name }));
  }, [form.type, getSubTypesForType, isReady, typeOptions]);

  // Calculate subcategory options using useMemo
  const subcategoryOptions = useMemo(() => {
    if (!form.category || !isReady) return [];

    const selectedCategory = categoryOptions.find(option => option.label === form.category);
    if (!selectedCategory) return [];

    const subcategories = getSubCategoriesForCategory(selectedCategory.value);
    return subcategories.map(item => ({ value: item.rowId, label: item.name }));
  }, [form.category, getSubCategoriesForCategory, isReady, categoryOptions]);

  // Memoize the handleChange function to prevent unnecessary re-renders
  const handleChange = useCallback((field: keyof CreateProductFormData, value: any) => {
    // Set dirty flag when any field changes
    setIsDirty(true);

    if (field === 'type') {
      setForm(prev => ({
        ...prev,
        [field]: value,
        subtype: '' // Reset subtype when type changes
      }));
    } else if (field === 'category') {
      setForm(prev => ({
        ...prev,
        [field]: value,
        subcategory: '' // Reset subcategory when category changes
      }));
    } else {

      if (field === 'name') {
        const nextValue = String(value ?? '');
        if (nextValue.trim().length === 0) {
          setErrors(prev => ({ ...prev, name: 'Name is required' }));
        } else {
          setErrors(prev => ({ ...prev, name: undefined }));
        }
      }
      // For all other fields, use the value as is
      setForm(prev => ({ ...prev, [field]: value }));
    }
  }, []);

  // Vendor modal handlers
  const handleOpenVendorModal = useCallback(() => setIsVendorModalOpen(true), []);
  const handleCloseVendorModal = useCallback(() => setIsVendorModalOpen(false), []);
  const handleSelectVendor = useCallback((vendor: VendorData) => {
    setForm(prev => ({
      ...prev,
      vendor: vendor.name, // Set the vendor name
      vendorId: vendor.rowId,
    }));
    console.log('Vendor selected:', vendor);
    setIsVendorModalOpen(false);
  }, []);

  // Brand modal handlers
  const handleOpenBrandModal = useCallback(() => setIsBrandModalOpen(true), []);
  const handleCloseBrandModal = useCallback(() => setIsBrandModalOpen(false), []);
  const handleSelectBrand = useCallback((brand: ManufacturerData) => {
    setForm(prev => ({
      ...prev,
      brand: brand.name,
      brandId: brand.rowId, // Set the brand rowID
    }));
    setIsBrandModalOpen(false);
  }, []);

  const handleCheckbox = useCallback((field: keyof CreateProductFormData) => {
    // Set dirty flag when any checkbox changes
    setIsDirty(true);
    setForm(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  const handleClear = useCallback(() => {
    setForm(defaultForm);
    setIsDirty(false); // Reset dirty flag when clearing
  }, []);

  const handleCreateProduct = useCallback(async () => {

    if (!isNameValid) {
      setErrors(prev => ({ ...prev, name: 'Name is required' }));
      document.getElementById('name')?.focus();
      return; // early exit
    }

    

    try {
      const transformedForm = transformFormDataToCreateProductData(form);

      await createProductApi(transformedForm).unwrap();

      // Reset dirty flag after successful creation
      setIsDirty(false);

      // Close modal on success
      onClose();

      // Optional: Show success message or redirect
      // You could add a toast notification here

    } catch (error) {
      console.error('Failed to create product:', error);
      // Handle error - show error message to user
      // You could add error handling here (toast, alert, etc.)
    }
  }, [form, createProductApi, onClose, isNameValid, errors]);

  // Cancel confirmation handlers
  const handleCancel = useCallback(() => {
    if (isDirty) {
      setShowCancelConfirmation(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleConfirmCancel = useCallback(() => {
    setForm(defaultForm); // Reset form
    setIsDirty(false);    // Reset dirty flag
    setShowCancelConfirmation(false);
    onClose();
  }, [onClose]);

  const handleCancelConfirmationClose = useCallback(() => {
    setShowCancelConfirmation(false);
  }, []);

  // Add loading state handling
  if (!isReady) {
    return (
      <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
        <ModalHeader toggle={onClose}>Pick Products</ModalHeader>
        <ModalBody>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          {error && (
            <div className="text-center mt-3">
              <p className="text-danger">{error}</p>
              <button className="btn btn-outline-primary btn-sm" onClick={retry}>
                Retry Loading Options
              </button>
            </div>
          )}
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Pick Products</ModalHeader>
      <ModalBody>
        <Form>
          <Row className="mb-2">
            <Col xs={12} md={6}>
              {/* <FormInput
                id="name"
                label="Name"
                value={form.name}
                onChange={val => handleChange('name', val)}
                required
              /> */}
              <FormInput
                id="name"
                label="Name"
                value={form.name}
                onChange={val => handleChange('name', val)}
                required
                aria-required="true"
                aria-invalid={!isNameValid}
                aria-describedby="name-error"
              />

              {!isNameValid && (
                <div id="name-error" className="text-danger small mt-1">
                  {errors.name || 'Name is required'}
                </div>
              )}
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={3}>
              <FormInput
                id="productCode"
                label="Product Code"
                value={form.productCode}
                onChange={val => handleChange('productCode', val)}
              />
            </Col>
            <Col md={3}>
              <FormInput
                id="vendor"
                label="Vendor"
                value={form.vendor}
                onChange={val => handleChange('vendor', val)}
                hasSearchButton
                onSearchClick={handleOpenVendorModal}
              />
            </Col>
            <Col md={3}>
              <FormInput
                id="vendorProductCode"
                label="V. Product Code"
                value={form.vendorProductCode}
                onChange={val => handleChange('vendorProductCode', val)}
              />
            </Col>
            <Col md={3}>
              <SelectInput
                formData={form}
                setFormData={setForm}
                label="Type"
                id="type"
                options={typeOptions.map((opt: { value: number; label: string }) => opt.label)}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={3}>
              <SelectInput
                formData={form}
                setFormData={setForm}
                label="Subtype"
                id="subtype"
                options={subtypeOptions.map((opt: { value: number; label: string }) => opt.label)}
                disabled={!form.type}
              />
            </Col>
            <Col md={3}>
              <SelectInput
                formData={form}
                setFormData={setForm}
                label="Margin Type"
                id="marginType"
                options={marginTypeOptions.map((opt: { value: number; label: string }) => opt.label)}
              />
            </Col>
            <Col md={3}>
              <SelectInput
                formData={form}
                setFormData={setForm}
                label="Category"
                id="category"
                options={categoryOptions.map((opt: { value: number; label: string }) => opt.label)}
              />
            </Col>
            <Col md={3}>
              <SelectInput
                formData={form}
                setFormData={setForm}
                label="Subcategory"
                id="subcategory"
                options={subcategoryOptions.map((opt: { value: number; label: string }) => opt.label)}
                disabled={!form.category}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={6}>
              <FormGroup>
                <Label className="form-label mb-0">Description</Label>
                <Input
                  type="textarea"
                  value={form.description}
                  onChange={e => handleChange('description', e.target.value)}
                  bsSize="sm"
                  rows={2}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={3}>
              <FormInput
                id="brand"
                label="Brand"
                value={form.brand}
                onChange={val => handleChange('brand', val)}
                hasSearchButton
                onSearchClick={handleOpenBrandModal}
              />
            </Col>
            <Col md={3}>
              <FormInput
                id="brandProductCode"
                label="Brand Product Code"
                value={form.brandProductCode}
                onChange={val => handleChange('brandProductCode', val)}
              />
            </Col>
            <Col md={3}>
              <FormInput
                id="barcode"
                label="Barcode"
                value={form.barcode}
                onChange={val => handleChange('barcode', val)}
              />
            </Col>
            <Col md={3}>
              <FormInput
                id="orderQty"
                label="Order Qty"
                value={form.orderQty}
                onChange={val => handleChange('orderQty', val)}
              />
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={2}>
              <FormInput
                id="purchaseExc"
                label="Purchase Exc"
                value={form.purchaseExc}
                onChange={val => handleChange('purchaseExc', val)}
                hasSearchButton
                iconClass="ri-calculator-line"
              />
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label className="form-label mb-0">Purchase Tax</Label>
                <Input
                  type="select"
                  value={form.purchaseTax}
                  onChange={e => handleChange('purchaseTax', e.target.value)}
                  bsSize="sm"
                >
                  {purchaseTaxOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormInput
                id="purchaseInc"
                label="Purchase Inc"
                value={form.purchaseInc}
                onChange={val => handleChange('purchaseInc', val)}
                hasSearchButton
                iconClass="ri-calculator-line"
              />
            </Col>
            <Col md={2}>
              <FormInput
                id="markup"
                label="Markup %"
                value={form.markup}
                onChange={val => handleChange('markup', val)}
              />
            </Col>
            <Col md={2}>
              <FormInput
                id="sellExc"
                label="Sell Exc"
                value={form.sellExc}
                onChange={val => handleChange('sellExc', val)}
                hasSearchButton
                iconClass="ri-calculator-line"
              />
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label className="form-label mb-0">Sell Tax</Label>
                <Input
                  type="select"
                  value={form.sellTax}
                  onChange={e => handleChange('sellTax', e.target.value)}
                  bsSize="sm"
                >
                  {sellTaxOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col md={2}>
              <FormInput
                id="sellInc"
                label="Sell Inc"
                value={form.sellInc}
                onChange={val => handleChange('sellInc', val)}
                hasSearchButton
                iconClass="ri-calculator-line"
              />
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label className="form-label mb-0">NZ Status</Label>
                <Input
                  type="select"
                  value={form.nzStatus}
                  onChange={e => handleChange('nzStatus', e.target.value)}
                  bsSize="sm"
                >
                  {nzStatusOptions.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormInput
                id="pickQuantity"
                label="Pick Quantity"
                value={form.pickQuantity}
                onChange={val => handleChange('pickQuantity', val)}
              />
            </Col>
            {/* Grouped checkboxes row */}
            <Row className="mb-2">
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.active}
                    onChange={() => handleCheckbox('active')}
                    bsSize="sm"
                    id="active"
                  />
                  <Label htmlFor="active" className="form-label mb-0">Active</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.orderable}
                    onChange={() => handleCheckbox('orderable')}
                    bsSize="sm"
                    id="orderable"
                  />
                  <Label htmlFor="orderable" className="form-label mb-0">Orderable</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.taxable}
                    onChange={() => handleCheckbox('taxable')}
                    bsSize="sm"
                    id="taxable"
                  />
                  <Label htmlFor="taxable" className="form-label mb-0">Taxable</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.addToAll}
                    onChange={() => handleCheckbox('addToAll')}
                    bsSize="sm"
                    id="addToAll"
                  />
                  <Label htmlFor="addToAll" className="form-label mb-0">Add to All</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.priceList}
                    onChange={() => handleCheckbox('priceList')}
                    bsSize="sm"
                    id="priceList"
                  />
                  <Label htmlFor="priceList" className="form-label mb-0">Price List</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.location}
                    onChange={() => handleCheckbox('location')}
                    bsSize="sm"
                    id="location"
                  />
                  <Label htmlFor="location" className="form-label mb-0">Location</Label>
                </FormGroup>
              </Col>
              <Col md="auto">
                <FormGroup check className="d-flex align-items-center gap-2 mb-0">
                  <Input
                    type="checkbox"
                    checked={form.dangerousGoods}
                    onChange={() => handleCheckbox('dangerousGoods')}
                    bsSize="sm"
                    id="dangerousGoods"
                  />
                  <Label htmlFor="dangerousGoods" className="form-label mb-0">Dangerous Goods</Label>
                </FormGroup>
              </Col>
            </Row>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-soft-secondary btn-sm" onClick={handleClear} disabled={isCreating}>Clear</button>
        <button
          className="btn btn-soft-primary btn-sm"
          onClick={handleCreateProduct}
          disabled={isCreating || !isNameValid}
        >
          {isCreating ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Creating...
            </>
          ) : (
            'Create'
          )}
        </button>
        <button className="btn btn-soft-success btn-sm" onClick={() => {/* TODO: implement create & pick */ }} disabled={isCreating || !isNameValid}>Create & Pick</button>
        <button className="btn btn-soft-info btn-sm" onClick={() => {/* TODO: implement generate barcode */ }} disabled={isCreating}>Generate Barcode</button>
        <button className="btn btn-soft-dark btn-sm" onClick={handleCancel} disabled={isCreating}>Cancel</button>
      </ModalFooter>

      {/* Add the modals here */}
      <AssociateVendorModal
        isOpen={isVendorModalOpen}
        onClose={handleCloseVendorModal}
        onSelect={handleSelectVendor}
      />

      <AssociateManufacturerModal
        isOpen={isBrandModalOpen}
        onClose={handleCloseBrandModal}
        onSelect={handleSelectBrand}
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmationModal
        show={showCancelConfirmation}
        variant="cancel"
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to cancel and lose your changes?"
        deleteButtonText="Yes, Cancel"
        cancelButtonText="Keep Editing"
        onDeleteClick={handleConfirmCancel}
        onCloseClick={handleCancelConfirmationClose}
      />
    </Modal>
  );
};

export default NewProductsModal;